import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ActivityReportExtendedModel, FileResponse, ParsedFileModel, TableBodyCellEntryModel, TableListIconModel } from '@shared/models';
import { NotificationService } from '@shared/services';

import { messages } from '@messages';
import { Weeks } from '@shared/factories';
import { IE_OPERATIONAL_TIME_NUMBER } from '@variables';

@Injectable()
export class HelperService {
  constructor (
    private http:                HttpClient,
    private notificationService: NotificationService
  ) { }

  parceTableListIconsWeb(iconsList: TableListIconModel[]): TableBodyCellEntryModel[] {
    return iconsList.map(el => ({
      internalOnly:    el.internalOnly,
      customerOnly:    el.customerOnly,
      routerLink:      el.linkTree,
      routerLinkState: el.linkState,
      class: el.show ? 'tooltip-hover' : 'hide events-none',
      children: [
        { class: `am-flex-center ${el.icon}` },
        { class: `web-tooltip text-s left short ${el.color}`, skip: !el.tooltipTexts?.length, children: el.tooltipTexts?.map(text => ({ class: 'tooltip-regular', value: text })) }
      ]
    }));
  }

  parceTableListIconsMobile(iconsList: TableListIconModel[]): TableBodyCellEntryModel[] {
    return iconsList.map(el => ({
      internalOnly: el.internalOnly,
      customerOnly: el.customerOnly,
      class: `${el.icon} ${el.show ? '' : 'hide'}`
    }));
  }

  callExternal(num: string): void {
    let tel;
    if (num) {
      if (num.charAt(0) === '0') tel = `+49${num.substring(1)}`;
      else if (num.substring(0,2) === '49' && num.length === 12) tel = `+${num}`;
      else tel = `+49${num}`;
    } else return this.notificationService.alert(messages.errors.validation.phoneNumberNotExist);

    let link = `tel:${tel}`;
    window.open(link, '_system');
  }

  private requestFile(url: string): Observable<FileResponse> {
    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      map(res => ({
        contentDisposition: res?.headers?.get('Content-Disposition'),
        blobBody:           res?.body
      }))
    );
  }

  private parseFile(contentDisposition: string, blobBody: Blob, mimeType: string): ParsedFileModel {
    const filename = this.extractFileNameFromContentDisposition(contentDisposition);
    const blob     = new Blob([blobBody], {type: mimeType});
    const blobUrl  = window.URL.createObjectURL(blob);

    return { filename, blob, blobUrl };
  }

  private extractFileNameFromContentDisposition(contentDisposition: string): string| null {
    if (!contentDisposition) {
      console.log("Content-Disposition header is empty or undefined");
      return null;
    }
    const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (!matches || matches.length < 2) {
      console.log("Filename not found in Content-Disposition header");
      return null;
    }
    return matches[1].replace(/['"]/g, '').trim();
  }

  loadFileAsBlobByUrl(url: string, type: string): Observable<ParsedFileModel> {
    return this.requestFile(url).pipe(
      map(({ contentDisposition, blobBody }) => this.parseFile(contentDisposition, blobBody, type))
    );
  }

  downloadFile({ filename, blobUrl }: ParsedFileModel): void {
    const link = document.createElement('a');
    link.href  = blobUrl;
    if (filename) link.download = filename;
    else {
      // If no filename is provided, set the target to '_blank'. This prevents Chrome from opening the file in the current tab's previewer,
      // ensuring it opens in a new tab, and the user can easily return to the app.
      link.target = '_blank';
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }

  downloadFileByUrl(url: string, type: string): Observable<void> {
    return this.requestFile(url).pipe(
      map(({ contentDisposition, blobBody }) => this.parseFile(contentDisposition, blobBody, type)),
      map(({ filename, blob, blobUrl      }) => this.downloadFile({ filename, blobUrl }))
    );
  }

  openPrintPreview({ blobUrl }: ParsedFileModel): void {
    // Open a new window
    const printWindow = window.open(blobUrl, '_blank');
    if (!printWindow) {
      throw new Error('Failed to open new window. Please check popup blockers.');
    }
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);
    };
    printWindow.document.close();
  }

  get isDevice(): boolean {
    return /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent)
        || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0,4))
  }

  isInstance<EntryType, InstanceType>(entry: EntryType, instance: new (...args: any[]) => InstanceType): InstanceType {
    return entry instanceof instance ? entry : null;
  }

  static checkIfValidPhotoTn(working_period: ActivityReportExtendedModel): boolean {
    return working_period.attachment_url && working_period.start_date && Weeks.getStartOfWeek(new Date(working_period.start_date)).getTime() + IE_OPERATIONAL_TIME_NUMBER > new Date().getTime();
  }

}
